import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';

import { all_routes } from '../router/all_routes';
import { AppDispatch, RootState } from '../../core/data/redux/store';
import {
  passwordReset,
  verifyOtp,
  resetAuth,
} from '../../core/data/redux/action';
import ImageWithBasePath from '../../core/data/img/ImageWithBasePath';
import Loader from '../../core/data/img/car_loading.json';

const routes = all_routes;

type FormValues = {
  email: string;
};

type OTPFormValues = {
  otp: string;
};

const ForgotPassword = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { otpVerifiedToken, success, loading, error } = useSelector(
    (state: RootState) => ({
      success: state.auth.success,
      error: state.auth.error,
      otpVerifiedToken: state.auth.otpVerifiedToken,
      loading: state.auth.loading,
    })
  );

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  });

  const {
    register: otpRegister,
    handleSubmit: otpHandleSubmit,
    formState: { errors: otpErrors },
    reset: otpReset,
  } = useForm<OTPFormValues>({
    defaultValues: {
      otp: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    dispatch(passwordReset(data));
  };

  const onOTPVerifySubmit: SubmitHandler<OTPFormValues> = (data) => {
    dispatch(
      verifyOtp({
        ...data,
        email: watch('email'),
      })
    );
  };

  const [toastMessage, setToastMessage] = useState('');
  const [toastClass, setToastClass] = useState('bg-success text-white');
  const [showOTPForm, setShowOTPForm] = useState(false);

  useEffect(() => {
    if (!otpVerifiedToken && success) {
      showToast(success);
      setShowOTPForm(true);
    } else if (otpVerifiedToken) {
      showToast(success);
      navigate(
        `${routes.resetPassword}?token=${encodeURIComponent(otpVerifiedToken)}&email=${watch('email')}`
      );
      otpReset();
      reset();
      dispatch(resetAuth());
    } else if (error) {
      showToast(error);
    }
  }, [success, otpVerifiedToken, error]);

  const showToast = (message: any) => {
    setToastClass(error ? 'bg-danger text-white' : 'bg-success text-white');

    setToastMessage(message);
  };

  const handleCloseToast = () => {
    dispatch(resetAuth());
    setToastMessage('');
  };

  useEffect(() => {
    if (toastMessage) {
      if (toastClass === 'bg-danger text-white') {
        toast.error(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      } else {
        toast.success(toastMessage, {
          position: 'top-right',
          hideProgressBar: true,
          onClose: handleCloseToast,
          progress: undefined,
          toastId: '',
        });
      }
    }
  }, [toastMessage, toastClass]);

  return (
    <>
      <div className="main-wrapper login-body">
        {/* Header */}
        <header className="log-header">
          <Link to={routes.homeOne}>
            <ImageWithBasePath
              className="img-fluid logo-dark"
              src="assets/img/logo/fairRentallogo.png"
              alt="Logo"
            />
          </Link>
        </header>

        {toastMessage && <ToastContainer autoClose={3000} />}

        <div className="login-wrapper  box-shadow-none  background-none">
          <div className="loginbox">
            <div className="login-auth">
              <div className="login-auth-wrap">
                <div className="sign-group">
                  <Link
                    to={routes.homeOne}
                    className="border-0 text-primary-dark d-flex align-items-center"
                  >
                    <i className="bx bx-arrow-back fs-5 me-2"></i>

                    <span className="mt-1"> Back To Home </span>
                  </Link>
                </div>
                <h1>{showOTPForm ? 'Verify OTP' : 'Forgot Password'}</h1>

                {showOTPForm ? (
                  <>
                    <form onSubmit={otpHandleSubmit(onOTPVerifySubmit)}>
                      <div className="input-block">
                        <label className="form-label">
                          OTP <span className="text-danger">*</span>
                        </label>

                        <input
                          type="otp"
                          className="form-control"
                          placeholder="Enter OTP"
                          {...otpRegister('otp', {
                            required: 'OTP is required',
                          })}
                        />
                        {otpErrors.otp && (
                          <p className="text-danger">{otpErrors.otp.message}</p>
                        )}
                      </div>

                      {!loading && (
                        <button
                          type="submit"
                          className="btn btn-primary w-100 btn-size mt-1 py-2"
                        >
                          Verify OTP
                        </button>
                      )}
                    </form>
                    {loading ? (
                      <div className="d-flex justify-content-center">
                        <Lottie
                          animationData={Loader}
                          loop={true}
                          style={{ width: 100, height: 100 }}
                        />
                      </div>
                    ) : (
                      <a
                        href="#"
                        className="text-center d-block py-1"
                        onClick={() => {
                          dispatch(passwordReset({ email: watch('email') }));
                        }}
                      >
                        {' '}
                        Resend OTP
                      </a>
                    )}
                  </>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="input-block mb-0">
                      <label className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>

                      <input
                        type="email"
                        className="form-control mb-1"
                        placeholder="Enter email"
                        {...register('email', {
                          required: 'Email is required',
                        })}
                      />
                      {errors.email && (
                        <p className="text-danger">{errors.email.message}</p>
                      )}
                    </div>

                    {loading ? (
                      <div className="d-flex justify-content-center">
                        <Lottie
                          animationData={Loader}
                          loop={true}
                          style={{ width: 100, height: 100 }}
                        />
                      </div>
                    ) : (
                      <button
                        type="submit"
                        disabled={isSubmitting || loading}
                        className="btn btn-primary w-100 btn-size mt-1 py-2"
                      >
                        Forget Password
                      </button>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <footer className="log-footer">
          <div className="container-fluid">
            {/* Copyright */}
            <div className="copyright">
              <div className="copyright-text">
                <p>© 2025 FaiRental. All Rights Reserved.</p>
              </div>
            </div>
            {/* /Copyright */}
          </div>
        </footer>
        {/* /Footer */}
      </div>
    </>
  );
};

export default ForgotPassword;
